import React, { useState, useEffect } from 'react';
import { getTrades } from '../../services/storage';
import { THEME } from '../../styles/theme';

// ── High-impact XAU/USD events database ──────────────────────────────────────
// Recurring patterns — Sir can add custom events on top of these.

const RECURRING_EVENTS = [
  // Weekly
  { id: 'jobless',  name: 'US Jobless Claims',   day: 4, time: '18:30', impact: 'MEDIUM', asset: 'USD', icon: '📋', desc: 'Weekly unemployment claims. Affects USD strength → gold inverse.' },
  // Monthly by week pattern
  { id: 'nfp',      name: 'Non-Farm Payrolls',   day: 5, week: 1, time: '18:30', impact: 'HIGH',   asset: 'USD', icon: '💥', desc: 'MOST important monthly event for gold. First Friday of month. Expect 50-100 pip spikes.' },
  { id: 'ism_mfg',  name: 'ISM Manufacturing',   day: 1, week: 1, time: '19:30', impact: 'MEDIUM', asset: 'USD', icon: '🏭', desc: 'First business day of month. Manufacturing PMI.' },
  { id: 'ism_svc',  name: 'ISM Services',        day: 3, week: 1, time: '19:30', impact: 'MEDIUM', asset: 'USD', icon: '🏢', desc: 'Third business day of month. Services PMI.' },
  { id: 'cpi',      name: 'US CPI Inflation',    day: 2, week: 2, time: '18:30', impact: 'HIGH',   asset: 'USD', icon: '🔥', desc: 'Mid-month. Core inflation drives Fed policy → HUGE gold mover.' },
  { id: 'ppi',      name: 'US PPI',              day: 2, week: 2, time: '18:30', impact: 'MEDIUM', asset: 'USD', icon: '📦', desc: 'Usually day before CPI. Producer price index.' },
  { id: 'retail',   name: 'US Retail Sales',     day: 2, week: 3, time: '18:30', impact: 'MEDIUM', asset: 'USD', icon: '🛍️', desc: 'Mid-month consumer spending data.' },
  { id: 'pce',      name: 'PCE Price Index',     day: 5, week: 4, time: '18:30', impact: 'HIGH',   asset: 'USD', icon: '📊', desc: "Fed's preferred inflation measure. End of month. Drives rate expectations." },
  // Quarterly
  { id: 'gdp',      name: 'US GDP (Prelim)',     day: 4, week: 4, time: '18:30', impact: 'HIGH',   asset: 'USD', icon: '🌍', desc: 'Quarterly. GDP growth rate affects Fed outlook.' },
  { id: 'fomc',     name: 'FOMC Rate Decision', special: true,   time: '23:30', impact: 'EXTREME', asset: 'FED', icon: '🏦', desc: '8 times per year. Fed interest rate decision + press conference. BIGGEST gold mover. Avoid trading 1hr before/after.' },
  { id: 'fomc_min', name: 'FOMC Minutes',       special: true,   time: '23:00', impact: 'HIGH',   asset: 'FED', icon: '📜', desc: '3 weeks after FOMC. Full meeting minutes released.' },
  // Gold specific
  { id: 'dxy',      name: 'DXY / Dollar Index', daily: true,     time: '14:30', impact: 'WATCH',  asset: 'DXY', icon: '💵', desc: 'Watch DXY direction daily. Gold is inverse to DXY (usually).' },
];

const IMPACT_COLORS = {
  EXTREME: '#ef4444',
  HIGH:    '#f97316',
  MEDIUM:  '#f59e0b',
  LOW:     '#6b7280',
  WATCH:   '#3b82f6',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const SK = 'jarvis_calendar_events';
function loadCustomEvents() { try { return JSON.parse(localStorage.getItem(SK) || '[]'); } catch { return []; } }
function saveCustomEvents(e) { localStorage.setItem(SK, JSON.stringify(e)); }

const S = {
  wrap:     { padding: '20px', maxWidth: '1000px', margin: '0 auto' },
  title:    { fontSize: '22px', fontWeight: '800', color: THEME.colors.blue, fontFamily: THEME.fonts.hud, letterSpacing: '2px', marginBottom: '6px' },
  sub:      { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginBottom: '24px' },
  tabs:     { display: 'flex', gap: '8px', marginBottom: '24px' },
  tab:      (a) => ({ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${a ? THEME.colors.blue : 'rgba(255,255,255,0.1)'}`, background: a ? 'rgba(0,212,255,0.1)' : 'transparent', color: a ? THEME.colors.blue : '#9ca3af', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }),

  // Week view
  weekNav:  { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  weekBtn:  { padding: '6px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '13px' },
  weekLabel:{ fontSize: '16px', fontWeight: '700', color: '#e2e8f0' },

  dayCol:   (isToday) => ({
    background:   isToday ? 'rgba(0,212,255,0.06)' : 'rgba(255,255,255,0.02)',
    border:       `1px solid ${isToday ? 'rgba(0,212,255,0.25)' : 'rgba(255,255,255,0.06)'}`,
    borderRadius: '10px',
    padding:      '12px',
    minHeight:    '120px',
  }),
  dayHead:  (isToday) => ({ fontSize: '12px', fontWeight: '700', color: isToday ? THEME.colors.blue : 'rgba(255,255,255,0.4)', marginBottom: '8px' }),
  dayGrid:  { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', marginBottom: '24px' },

  eventPill:(impact) => ({
    background:   IMPACT_COLORS[impact] + '22',
    border:       `1px solid ${IMPACT_COLORS[impact]}44`,
    borderRadius: '6px',
    padding:      '5px 8px',
    marginBottom: '4px',
    cursor:       'pointer',
  }),
  eName:    { fontSize: '11px', fontWeight: '700', color: '#e2e8f0', lineHeight: '1.3' },
  eTime:    { fontSize: '10px', color: 'rgba(255,255,255,0.35)' },
  eBadge:   (impact) => ({ display: 'inline-block', fontSize: '9px', fontWeight: '800', color: IMPACT_COLORS[impact], letterSpacing: '1px' }),

  // Event detail modal
  modal:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalBox: { background: '#0f172a', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '14px', padding: '24px', maxWidth: '500px', width: '100%' },
  modalTitle:{ fontSize: '18px', fontWeight: '800', color: '#fff', marginBottom: '6px' },
  modalMeta: { fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '16px' },
  modalDesc: { fontSize: '14px', color: '#9ca3af', lineHeight: '1.6', marginBottom: '16px' },
  modalClose:{ padding: '8px 20px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '13px' },

  // Add event form
  form:     { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '16px', marginBottom: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' },
  label:    { fontSize: '11px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '4px' },
  input:    { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  select:   { width: '100%', padding: '9px 12px', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  addBtn:   { padding: '9px 20px', borderRadius: '8px', border: 'none', background: THEME.colors.blue, color: '#000', cursor: 'pointer', fontWeight: '700', fontSize: '13px' },

  // Stats
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' },
  statCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '14px' },
  statLbl:  { fontSize: '10px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '4px' },
  statVal:  (c='#fff') => ({ fontSize: '18px', fontWeight: '700', color: c }),
  statSub:  { fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' },
};

// Get date for a recurring event in a specific month/year
function getEventDate(ev, year, month) {
  if (ev.daily) return null; // daily events don't have a fixed date
  if (ev.special) return null; // FOMC dates are set manually

  const dates = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  for (let d = 1; d <= daysInMonth; d++) {
    const dt = new Date(year, month, d);
    const dayOfWeek = dt.getDay();

    if (ev.day !== undefined && dayOfWeek !== ev.day) continue;

    if (ev.week) {
      // nth occurrence of day in month
      let count = 0;
      for (let i = 1; i <= d; i++) {
        if (new Date(year, month, i).getDay() === ev.day) count++;
      }
      if (count === ev.week) { dates.push(d); break; }
    } else {
      // every week
      dates.push(d);
    }
  }
  return dates;
}

export default function EconomicCalendar() {
  const [tab,         setTab]         = useState('week');
  const [weekOffset,  setWeekOffset]  = useState(0);
  const [selected,    setSelected]    = useState(null);
  const [custom,      setCustom]      = useState(loadCustomEvents);
  const [form,        setForm]        = useState({ name: '', date: '', time: '18:30', impact: 'MEDIUM', notes: '' });
  const trades = getTrades().filter(t => t.result && t.result !== 'OPEN');

  // Get week dates
  const today     = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay() + weekOffset * 7);

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    return d;
  });

  // Build events for each day of the week
  function getEventsForDate(date) {
    const year  = date.getFullYear();
    const month = date.getMonth();
    const day   = date.getDate();
    const dow   = date.getDay();

    const events = [];

    // Check recurring events
    RECURRING_EVENTS.forEach(ev => {
      if (ev.daily) {
        events.push({ ...ev, date });
        return;
      }
      if (ev.special) return; // handled manually
      if (ev.day !== undefined && ev.week) {
        const dates = getEventDate(ev, year, month);
        if (dates && dates.includes(day)) events.push({ ...ev, date });
      } else if (ev.day !== undefined && !ev.week) {
        // Every week on this day
        if (dow === ev.day) events.push({ ...ev, date });
      }
    });

    // Custom events for this date
    const dateStr = date.toISOString().split('T')[0];
    custom.filter(e => e.date === dateStr).forEach(e => events.push({ ...e, date }));

    return events;
  }

  // Win rate correlation: trades on high-impact days vs normal days
  function getEventCorrelation() {
    const eventDates = new Set();
    const year = today.getFullYear();
    for (let m = 0; m < 12; m++) {
      RECURRING_EVENTS.forEach(ev => {
        if (!ev.day || !ev.week) return;
        const dates = getEventDate(ev, year, m);
        if (dates) dates.forEach(d => {
          eventDates.add(`${year}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`);
        });
      });
    }
    const eventTrades  = trades.filter(t => eventDates.has(t.date?.split('T')[0] || t.date?.split(' ')[0]));
    const normalTrades = trades.filter(t => !eventDates.has(t.date?.split('T')[0] || t.date?.split(' ')[0]));

    const wr = (arr) => arr.length ? Math.round(arr.filter(t => t.result === 'WIN').length / arr.length * 100) : null;
    return { eventWR: wr(eventTrades), normalWR: wr(normalTrades), eventCount: eventTrades.length, normalCount: normalTrades.length };
  }

  const corr = getEventCorrelation();

  const addCustom = () => {
    if (!form.name || !form.date) return;
    const entry = { ...form, id: `CE${Date.now()}` };
    const next = [...custom, entry];
    setCustom(next);
    saveCustomEvents(next);
    setForm({ name: '', date: '', time: '18:30', impact: 'MEDIUM', notes: '' });
  };

  const deleteCustom = (id) => {
    const next = custom.filter(e => e.id !== id);
    setCustom(next);
    saveCustomEvents(next);
  };

  const todayStr = today.toISOString().split('T')[0];
  const todayEvents = getEventsForDate(today);
  const highImpactToday = todayEvents.filter(e => ['HIGH','EXTREME'].includes(e.impact));

  return (
    <div style={S.wrap}>
      <div style={S.title}>ECONOMIC CALENDAR</div>
      <div style={S.sub}>
        High-impact news events for XAU/USD. Correlated with your trade history.
      </div>

      {/* Today's alert */}
      {highImpactToday.length > 0 && (
        <div style={{ background: '#ef444411', border: '1px solid #ef444433', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#ef4444', marginBottom: '6px' }}>
            ⚠️ HIGH IMPACT EVENTS TODAY
          </div>
          {highImpactToday.map(e => (
            <div key={e.id} style={{ fontSize: '13px', color: '#fca5a5', marginBottom: '4px' }}>
              {e.icon} {e.name} @ {e.time} IST — {e.desc}
            </div>
          ))}
        </div>
      )}

      {/* Correlation stats */}
      {trades.length >= 5 && (
        <div style={S.statGrid}>
          {[
            { label: 'WIN RATE — EVENT DAYS',  value: corr.eventWR  != null ? corr.eventWR  + '%' : '—', col: corr.eventWR  >= 50 ? '#10b981' : '#ef4444', sub: `${corr.eventCount} trades` },
            { label: 'WIN RATE — NORMAL DAYS', value: corr.normalWR != null ? corr.normalWR + '%' : '—', col: corr.normalWR >= 50 ? '#10b981' : '#ef4444', sub: `${corr.normalCount} trades` },
            { label: 'RECOMMENDATION',
              value: corr.eventWR != null && corr.normalWR != null
                ? corr.eventWR < corr.normalWR - 10 ? 'Avoid News Days' : corr.eventWR > corr.normalWR + 10 ? 'Trade News Days' : 'No Clear Edge'
                : '—',
              col: '#f59e0b', sub: 'Based on your history' },
          ].map(s => (
            <div key={s.label} style={S.statCard}>
              <div style={S.statLbl}>{s.label}</div>
              <div style={S.statVal(s.col)}>{s.value}</div>
              <div style={S.statSub}>{s.sub}</div>
            </div>
          ))}
        </div>
      )}

      <div style={S.tabs}>
        {[['week','Week View'],['add','Add Event'],['list','Event Guide']].map(([id,label]) => (
          <button key={id} style={S.tab(tab===id)} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {/* Week view */}
      {tab === 'week' && (
        <>
          <div style={S.weekNav}>
            <button style={S.weekBtn} onClick={() => setWeekOffset(o => o - 1)}>← Prev</button>
            <div style={S.weekLabel}>
              {weekDates[0].getDate()} {MONTHS[weekDates[0].getMonth()]} — {weekDates[6].getDate()} {MONTHS[weekDates[6].getMonth()]} {weekDates[0].getFullYear()}
            </div>
            <button style={S.weekBtn} onClick={() => setWeekOffset(o => o + 1)}>Next →</button>
            <button style={{ ...S.weekBtn, color: THEME.colors.blue }} onClick={() => setWeekOffset(0)}>Today</button>
          </div>
          <div style={S.dayGrid}>
            {weekDates.map((date, i) => {
              const isToday = date.toISOString().split('T')[0] === todayStr;
              const events  = getEventsForDate(date);
              return (
                <div key={i} style={S.dayCol(isToday)}>
                  <div style={S.dayHead(isToday)}>
                    {DAYS[date.getDay()]} {date.getDate()}
                    {isToday && ' ●'}
                  </div>
                  {events.slice(0, 4).map((ev, j) => (
                    <div key={j} style={S.eventPill(ev.impact)} onClick={() => setSelected(ev)}>
                      <div style={S.eName}>{ev.icon} {ev.name}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={S.eTime}>{ev.time}</span>
                        <span style={S.eBadge(ev.impact)}>{ev.impact}</span>
                      </div>
                    </div>
                  ))}
                  {events.length > 4 && (
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>+{events.length - 4} more</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Add custom event */}
      {tab === 'add' && (
        <div>
          <div style={S.form}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: THEME.colors.blue, marginBottom: '14px' }}>Add Custom Event</div>
            <div style={S.formGrid}>
              <div>
                <div style={S.label}>EVENT NAME</div>
                <input style={S.input} placeholder="FOMC Meeting, RBI Policy..." value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}/>
              </div>
              <div>
                <div style={S.label}>DATE</div>
                <input style={S.input} type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))}/>
              </div>
              <div>
                <div style={S.label}>TIME (IST)</div>
                <input style={S.input} placeholder="18:30" value={form.time} onChange={e => setForm(f => ({...f, time: e.target.value}))}/>
              </div>
              <div>
                <div style={S.label}>IMPACT</div>
                <select style={S.select} value={form.impact} onChange={e => setForm(f => ({...f, impact: e.target.value}))}>
                  {Object.keys(IMPACT_COLORS).map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <div style={S.label}>NOTES</div>
              <input style={S.input} placeholder="What to watch for..." value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}/>
            </div>
            <button style={S.addBtn} onClick={addCustom}>Add Event</button>
          </div>

          {custom.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '12px' }}>YOUR CUSTOM EVENTS</div>
              {custom.map(e => (
                <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', marginBottom: '6px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div>
                    <span style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: '600' }}>{e.name}</span>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginLeft: '10px' }}>{e.date} {e.time}</span>
                    <span style={{ fontSize: '10px', fontWeight: '700', color: IMPACT_COLORS[e.impact], marginLeft: '8px' }}>{e.impact}</span>
                  </div>
                  <button style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #ef444433', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: '11px' }} onClick={() => deleteCustom(e.id)}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Event guide */}
      {tab === 'list' && (
        <div>
          {RECURRING_EVENTS.map(ev => (
            <div key={ev.id} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${IMPACT_COLORS[ev.impact]}22`, borderRadius: '10px', padding: '14px 16px', marginBottom: '8px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
              <div style={{ fontSize: '24px', flexShrink: 0 }}>{ev.icon}</div>
              <div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#e2e8f0' }}>{ev.name}</span>
                  <span style={{ fontSize: '10px', fontWeight: '800', color: IMPACT_COLORS[ev.impact], letterSpacing: '1px' }}>{ev.impact}</span>
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>{ev.time} IST</span>
                </div>
                <div style={{ fontSize: '13px', color: '#9ca3af', lineHeight: '1.5' }}>{ev.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Event detail modal */}
      {selected && (
        <div style={S.modal} onClick={() => setSelected(null)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>{selected.icon} {selected.name}</div>
            <div style={S.modalMeta}>
              <span style={{ color: IMPACT_COLORS[selected.impact], fontWeight: '700' }}>{selected.impact} IMPACT</span>
              &nbsp;·&nbsp;{selected.time} IST
            </div>
            <div style={S.modalDesc}>{selected.desc || selected.notes || 'No description'}</div>
            <button style={S.modalClose} onClick={() => setSelected(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
